import {cn} from '@/lib/utils.ts';
import {type Cell, flexRender, type Header, type Row, type RowData} from '@tanstack/react-table';
import {ChevronDown, ChevronUp} from 'lucide-react';
import {type CSSProperties, Fragment, type ReactNode, useEffect, useState,} from 'react';
import {Button} from '@/components/ui/button.tsx';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow,} from '@/components/ui/table.tsx';
import {Spinner} from '@/components/ui/spinner.tsx';
import {AppTableColumnHeader} from '@/components/table/app-table-column-header.tsx';
import type {UseQueryResult} from '@tanstack/react-query';
import { UseAppTableReturn } from '@/components/table/use-app-table.ts';
import { AppPagination } from '@/components/table/app-pagination.tsx';


// noinspection JSUnusedGlobalSymbols
export const ExpandRowCell = ({row}: { row: Row<any> }) => {
  return row.getIsExpanded() ? (
    <Button variant="ghost" size="icon" onClick={(e) => {
      row.toggleExpanded();
      e.stopPropagation();
    }}>
      <ChevronUp/>
    </Button>
  ) : (
    <Button variant="ghost" size="icon" onClick={(e) => {
      row.toggleExpanded();
      e.stopPropagation();
    }}>
      <ChevronDown/>
    </Button>
  );
};

function getWidth(header: any) {
  const width = header.column.columnDef.meta?.width;
  if (width) {
    return `${width}px`;
  }
  const size = header.column.columnDef.size;
  if (size !== 999 && size !== 150) {
    return `${size}px`;
  }
  return 'auto';
}

function getCellClassName(cell: Cell<any, any>) {
  const classes = [];
  if (cell.column.columnDef.meta?.className) {
    classes.push(cell.column.columnDef.meta.className);
  }
  if (cell.column.columnDef.meta?.cellClassName) {
    classes.push(cell.column.columnDef.meta.cellClassName);
  }
  const align = cell.column.columnDef.meta?.align;
  if (align === 'right') {
    classes.push('text-right');
  }
  if (align === 'center') {
    classes.push('text-center');
  }
  return classes.join(' ');
}

function getHeaderClassName(header: Header<any, any>) {
  const classes = [];
  if (header.column.columnDef.meta?.className) {
    classes.push(header.column.columnDef.meta.className);
  }
  const align = header.column.columnDef.meta?.hAlign;
  if (align === 'right') {
    classes.push('text-right');
  }
  if (align === 'center') {
    classes.push('text-center');
  }
  return classes.join(' ');
}

function getCell(cell: Cell<any, any>) {
  const wrapDiv = cell.column.columnDef.meta?.wrapDiv;
  if (wrapDiv) {
    return (
      <div className={wrapDiv}>
        {flexRender(
          cell.column.columnDef.cell,
          cell.getContext(),
        )}
      </div>
    );
  }
  return flexRender(
    cell.column.columnDef.cell,
    cell.getContext(),
  );
}

function getIsLoading(query?: UseQueryResult<any>): boolean {
  if (!query) {
    return false;
  }
  return query.isLoading || query.isPlaceholderData;
}

type Props<T extends RowData> = {
  at: UseAppTableReturn<T>;
  className?: string;
  getExpanded?: (original: T) => ReactNode;
  onRowClick?: (row: T) => void;
  minWidth?: number;
  status?: 'error' | 'success' | 'pending';
  rowClickExpand?: boolean;
  resetExpandedOnRowChange?: boolean;
  spacerRow?: boolean;
  paginationClassName?: string;
};

export function AppTable<T extends RowData>({
  at,
  className,
  getExpanded,
  minWidth = -1,
  rowClickExpand = true,
  resetExpandedOnRowChange = true,
  onRowClick,
  spacerRow = true,
}: Props<T>) {
  const [colCount, setColCount] = useState(0);
  if (!getExpanded) {
    rowClickExpand = false;
  }
  useEffect(() => {
    if (colCount == 0 && at.table.getRowModel().rows.length > 0) {
      if (resetExpandedOnRowChange) {
        at.table.resetExpanded();
      }
      setColCount(at.table.getAllColumns().length);
    }
  }, [at.table.getRowModel().rows]);

  const style: CSSProperties = {};
  if (minWidth !== -1) {
    style.minWidth = `${minWidth}px`;
  }

  return (
    <>
      <div className={cn('app-table-wrap flex-1 relative overflow-y-auto', className)}>
        <div className="absolute top-5 right-0 left-0 z-20 flex justify-center">
          <div
            className={`fixed opacity-70 ${!getIsLoading(at.query) && 'hidden'}`}
          >
            <Spinner className="size-8"/>
          </div>
        </div>
        <Table
          noWrapper
          className="w-full app-table border-collapse"
          style={style}
          border={0}
          cellSpacing={0}
          cellPadding={0}
        >
          <TableHeader className="bg-background sticky top-0">
            {at.table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-bg-1">
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn('first:rounded-l-md last:rounded-r-md p-1 border-bg-1', getHeaderClassName(header))}
                    style={{
                      width: getWidth(header), // minimum size is 20
                    }}
                  >
                    <AppTableColumnHeader header={header}/>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {spacerRow && (
              <TableRow
                key={'first-spacer'}
                className="spacer-row hover:bg-transparent"
              >
                <TableCell colSpan={colCount}></TableCell>
              </TableRow>
            )}
            {at.table.getRowModel().rows.map((row) => (
              <Fragment key={row.id}>
                <TableRow
                  key={row.id}
                  className={cn(
                    'item-row mb-2',
                    row.getIsSelected() && 'selected',
                    rowClickExpand && row.getIsExpanded() && 'expanded',
                    (Boolean(rowClickExpand) || Boolean(onRowClick)) &&
                    'cursor-pointer',
                  )}
                  onClick={() => {
                    if (rowClickExpand) {
                      row.toggleExpanded();
                    } else if (onRowClick) {
                      onRowClick(row.original);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn('p-2 bg-bg-2 whitespace-normal', getCellClassName(cell))}
                    >
                      {getCell(cell)}
                    </TableCell>
                  ))}
                </TableRow>
                {row.getIsExpanded() && typeof getExpanded === 'function' && (
                  <TableRow key={row.id + '-details'} className="expanded-row">
                    <TableCell
                      colSpan={row.getVisibleCells().length}
                      className="p-0"
                    >
                      {getExpanded(row.original)}
                    </TableCell>
                  </TableRow>
                )}
                {spacerRow && (
                  <TableRow
                    key={row.id + '-spacer'}
                    className="spacer-row hover:bg-transparent"
                  >
                    <TableCell colSpan={colCount}></TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
      {at.pagination && <AppPagination at={at}/>}
    </>
  );
}
